import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, AlertTriangle, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";

const DOCUMENT_CATEGORIES = {
  vessel: {
    title: "Vessel Documents",
    icon: "⚓",
    docs: [
      { id: "registration", label: "Registration Certificate", required: true },
      { id: "insurance", label: "Insurance Certificate", required: true },
      { id: "mmsi", label: "MMSI / Call Sign", required: false }
    ]
  },
  crew: {
    title: "Crew Documentation",
    icon: "👥",
    docs: [
      { id: "crew-list", label: "Crew List", required: true },
      { id: "passports", label: "Crew Passports", required: true }
    ]
  },
  safety: {
    title: "Safety Equipment",
    icon: "🛟",
    docs: [
      { id: "safety-equip", label: "Safety Equipment Certificate", required: true },
      { id: "vhf-license", label: "VHF License", required: false }
    ]
  },
  customs: {
    title: "Customs & Immigration",
    icon: "🔖",
    docs: [
      { id: "customs-form", label: "Customs Declaration Form", required: true }
    ]
  }
};

export default function PortEntryPackForm({ port, trip, user, onSubmit, onBack }) {
  const [documents, setDocuments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showMissingAlert, setShowMissingAlert] = useState(false);

  const toggleDocument = (docId) => {
    setDocuments(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };

  const getComplianceRisk = () => {
    const allRequired = Object.values(DOCUMENT_CATEGORIES).flatMap(cat =>
      cat.docs.filter(d => d.required).map(d => d.id)
    );
    const missing = allRequired.filter(id => !documents[id]);
    const percent = ((allRequired.length - missing.length) / allRequired.length) * 100;

    if (percent === 100) return { level: "ready", color: "bg-green-100 text-green-800", emoji: "🟢" };
    if (percent >= 70) return { level: "warning", color: "bg-yellow-100 text-yellow-800", emoji: "🟡" };
    return { level: "risk", color: "bg-red-100 text-red-800", emoji: "🔴" };
  };

  const risk = getComplianceRisk();

  const getMissingRequired = () => {
    return Object.values(DOCUMENT_CATEGORIES).flatMap(cat =>
      cat.docs.filter(d => d.required && !documents[d.id]).map(d => d.label)
    );
  };

  const handleSubmit = async () => {
    const missing = getMissingRequired();
    if (missing.length > 0) {
      setShowMissingAlert(true);
      return;
    }
    setShowMissingAlert(false);
    setSubmitting(true);
    setTimeout(() => {
      onSubmit({
        port,
        trip,
        documents,
        submittedAt: new Date(),
        completionPercent: getCompletionPercent()
      });
      setSubmitting(false);
    }, 1500);
  };

  const getCompletionPercent = () => {
    const total = Object.values(DOCUMENT_CATEGORIES).flatMap(c => c.docs).length;
    const completed = Object.values(documents).filter(Boolean).length;
    return Math.round((completed / total) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Port Header */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold">{port.name}</h2>
                <p className="text-sm text-muted-foreground">{port.location}, {port.country}</p>
              </div>
              <Badge className={`${risk.color}`}>{risk.emoji} {getCompletionPercent()}% Complete</Badge>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Required Documents */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        <div className="space-y-4">
          <h3 className="text-sm font-semibold uppercase text-muted-foreground">Required Documents</h3>

          {Object.entries(DOCUMENT_CATEGORIES).map(([catId, category], idx) => (
            <motion.div
              key={catId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + idx * 0.05 }}
            >
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span>{category.icon}</span> {category.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {category.docs.map(doc => {
                    const hasDoc = documents[doc.id];
                    return (
                      <button
                        key={doc.id}
                        onClick={() => { toggleDocument(doc.id); setShowMissingAlert(false); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          hasDoc
                            ? "border-green-300 bg-green-50 dark:bg-green-950/30"
                            : showMissingAlert && doc.required
                            ? "border-red-400 bg-red-50 dark:bg-red-950/20"
                            : "border-border hover:border-muted-foreground"
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">{doc.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.required ? "Required" : "Optional"}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          {hasDoc ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : doc.required ? (
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-border rounded-full" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Entry Form Preview */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">📋 Port Submission Form</CardTitle>
            <CardDescription>Auto-filled from your vessel & trip info</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Vessel</p>
                <p className="font-semibold">{trip.boat.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Flag State</p>
                <p className="font-semibold">US 🇺🇸</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ETA</p>
                <p className="font-semibold">{trip.eta} {trip.etaTime}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Crew</p>
                <p className="font-semibold">{trip.crewCount} person{trip.crewCount > 1 ? "s" : ""}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Compliance Risk Indicator */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
        <Card className={`${risk.color} border-2`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Entry Status: {risk.level === "ready" ? "✓ Ready" : risk.level === "warning" ? "⚠️ Warning" : "🔴 High Risk"}</p>
                <p className="text-xs mt-1">
                  {risk.level === "ready"
                    ? "All required documents complete. You can submit now."
                    : risk.level === "warning"
                    ? "You have most documents. Minor items missing."
                    : "Critical documents missing. You may be denied entry."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Missing docs alert */}
      {showMissingAlert && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700 p-4 flex gap-3 items-start">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-red-700 dark:text-red-400 mb-1">Missing required documents:</p>
              <ul className="list-disc list-inside space-y-0.5 text-red-600 dark:text-red-400 text-xs">
                {getMissingRequired().map(label => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end sticky bottom-0 bg-background pt-4 border-t border-border">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button variant="outline" className="gap-2">
          📄 Export PDF
        </Button>
        <Button onClick={handleSubmit} disabled={submitting} className="gap-2">
          {submitting ? "Submitting..." : "📤 Submit to Port"}
        </Button>
      </div>
    </div>
  );
}